import { useEffect, type RefObject } from 'react';

/** Offscreen galleries release their decoder and buffers while the interactive map is in use. */
export function useVisibleSampleVideo(video: RefObject<HTMLVideoElement | null>, source: string) {
  useEffect(() => {
    const player = video.current;
    if (!player) return;
    let visible = typeof IntersectionObserver === 'undefined';
    let position = 0;
    const release = () => {
      if (!player.hasAttribute('src')) return;
      position = player.currentTime;
      player.pause(); player.removeAttribute('src'); player.load();
    };
    const update = () => {
      if (!visible || document.hidden) { release(); return; }
      if (player.getAttribute('src') !== source) { player.src = source; player.load(); }
    };
    const restore = () => {
      if (position > 0 && Number.isFinite(player.duration)) player.currentTime = Math.min(position, player.duration);
    };
    const observer = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver(entries => {
      visible = entries.some(entry => entry.isIntersecting); update();
    }, { rootMargin: '200px 0px' });
    observer?.observe(player);
    document.addEventListener('visibilitychange', update);
    player.addEventListener('loadedmetadata', restore);
    update();
    return () => {
      observer?.disconnect(); document.removeEventListener('visibilitychange', update);
      player.removeEventListener('loadedmetadata', restore); release();
    };
  }, [video, source]);
}
